/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type UntypedFormControl, type ValidationErrors } from '@angular/forms';
import { type BaseRequestOptionsArgs, type HttpResponse, type Rest, HttpService, UNPLACEHOLDER } from '@dm/core';
import { type Observable } from 'rxjs';

import { type Dtree, type FileSystem } from './../../../../common/types/mo.d';

@Injectable({ providedIn: 'root' })
export class DtreeService {
    constructor(
        private http: HttpService,
    ) { }

    /**
     * 获取批量查询的rest
     * @param parentId DtreeID
     */
    getBatchRest(parentId: string, shareType?: string, vstoreId?: string): Rest {
        const params = {
            PARENTID: parentId,
            shareType,
            vstoreId,
        };
        if (!shareType) {
            delete params.shareType;
        }
        return this.http.makeUrl('dtree.getBatch').setParams(params);
    }

    /**
     * 查询单个Dtree
     * @param id
     */
    get(id: string, vstoreId?: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('dtree.get').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.get(rest);
    }

    /**
     * 创建Dtree
     * @param params
     * @param options
     */
    create(params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('dtree.post');
        return this.http.post(rest, params, options);
    }

    /**
     * 修改Dtree
     * @param id
     * @param params
     * @param options
     */
    modify(id: string, params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('dtree.put').format(id);
        return this.http.put(rest, params, options);
    }

    /**
     * 删除Dtree
     * @param id
     * @param options
     */
    delete(id: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('dtree.delete').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.delete(rest, options);
    }

    /**
     * 查询文件系统rest
     */
    getFileSystemRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('fileSystem.getBatch', undefined, true);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    /**
     * Dtree名字校验
     */
    nameValidator(control: UntypedFormControl): ValidationErrors | null {
        const value: string = control.value;
        const invalidReg = /[:\/,\t]/;
        const periodOnlyReg = /^[\.]{1,2}$/;
        const isValid = !invalidReg.test(value) && !periodOnlyReg.test(value);
        return isValid ? null : { specailInclude: true };
    }

    /**
     * 获取dtree全路径
     */
    getDtreeFullPath(fileSystem: FileSystem, dtree: Dtree): string {
        if (!fileSystem) {
            return UNPLACEHOLDER;
        }
        if (!dtree.path) {
            return UNPLACEHOLDER;
        }
        // 容错处理，如果没有返回dtreeDirName，就直接拼dtree名字
        if (!dtree.dtreeDirName) {
            dtree.dtreeDirName = dtree.NAME;
        }
        // 如果是文件系统根目录，直接拼dtree目录
        if (dtree.path === '/') {
            return '/' + fileSystem.NAME + '/' + dtree.dtreeDirName;
        }
        return '/' + fileSystem.NAME + dtree.path + '/' + dtree.dtreeDirName;
    }
}
